import {
  memo,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState
} from 'react';

import type { RJSFSchema, UiSchema } from '@rjsf/utils';
import filesize from 'filesize';
import { CloudUpload } from 'lucide-react';
import Dropzone, { type DropFilesEventHandler } from 'react-dropzone';
import shortid from 'shortid';

import { Button } from '@/shadcn/button';
import { Label } from '@/shadcn/label';
import { cn } from '@/shadcn/lib/utils';
import { Switch } from '@/shadcn/switch';

import type { WidgetWriterProps } from '@/components/forms/components/form/writer/widgets/widget.type';
import {
  FormDescription,
  FormHelp,
  FormLabel
} from '@/components/forms/ui/ui.form';
import { isLockedWidget } from '@/components/forms/utils/links';
import { useDynamicProps } from '@/hooks/useDynamicProps';
import { usePropsSynchronization } from '@/hooks/usePropsSynchronization';
import { AcceptedFilesDescription } from './acceptedFilesDescription/acceptedFilesDescription.fileUpload';
import {
  ADD_FILES,
  ERROR,
  PROGRESS,
  REMOVE_FILE,
  SUCCESS
} from './constant/actions';
import { ERROR_TYPE_EXTENSION, ERROR_TYPE_SIZE } from './constant/errors';
import { File } from './file/file.fileUpload';
import { fileUploadUiOptionsSchema } from './fileUpload.schema';
import { Label as FieldLabel } from './label/label.fileUpload';
import { reducer } from './reducer';
import type { FileType } from './types';
import { isExtensionAllowed, restrictWhitelistMap } from './utils';

// ? This is not actually a Widget but a field,
// ? for convience we use the same type as WidgetProps
type Props = WidgetWriterProps<FileType[], RJSFSchema> & {
  uiSchema: UiSchema<{
    'ui:options': {
      visibilitySwitchLabel?: string;
    };
  }>;
  visibilitySwitchLabel?: string;
  formData?: FileType[];
  onChange: (files: FileType[]) => void;
};

export const FileUploadField = memo((rjsfProps: Props) => {
  const props = useDynamicProps(rjsfProps);

  usePropsSynchronization<any, any>(props, rjsfProps);

  const {
    schema: { title },
    formContext: { uploadFile, uploadConfig, links },
    formData,
    onChange,
    required,
    idSchema,
    uiSchema
  } = props;

  const [files, dispatch] = useReducer(reducer, formData || []);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!files) return;
    onChange(files);
  }, [files]);

  // check if the widget is locked because of forms links
  const fieldId = idSchema.$id;
  const isLocked = isLockedWidget(fieldId, links);
  const isDisabled = props.disabled || isLocked;

  const [dropzoneActive, setDropzoneActive] = useState(false);

  let dropzoneRef: any | undefined = useRef(null);

  /**
   * Count constants in order to display number of:
   * - files uploaded
   * - error files
   */
  const filesCount = files.length;
  const errorsCount = files.filter(f => f.error).length;

  // Set to fit within the batched forms

  const maxFileSize =
    (uploadConfig && uploadConfig.maxFileSize) || 1024 * 1024 * 10;

  const { acceptedExtensions } = fileUploadUiOptionsSchema.parse(
    uiSchema['ui:options'] ?? {}
  );

  // media-api should be feeding this map through the client (e.g: trace-ui)
  const fileExtensionWhitelistMap = restrictWhitelistMap(
    (uploadConfig && uploadConfig.fileExtensionWhitelistMap) || null,
    acceptedExtensions
  );

  /**
   * @function handleNewFiles - handles the files that are dropped
   * or selected using react-dropzone
   * @param {Array.<Object>} acceptedFiles
   */
  const onDropNewFiles: DropFilesEventHandler = useCallback(
    acceptedFiles => {
      if (isDisabled) return;

      const allowedExtensions = fileExtensionWhitelistMap
        ? Object.values(fileExtensionWhitelistMap).flat(1)
        : undefined;

      const newFiles = acceptedFiles.filter(
        f => !files.find(i => f.name === i.name)
      );

      onDragLeave();
      if (!newFiles.length) return;

      const forbiddenFileExtensions = acceptedFiles.filter(
        file => !isExtensionAllowed(allowedExtensions, file.name)
      );

      const exceedSizeLimit = acceptedFiles.filter(
        file =>
          file.size > maxFileSize && !forbiddenFileExtensions.includes(file)
      );

      const readyToUpload = acceptedFiles.filter(
        file =>
          !(
            exceedSizeLimit.includes(file) ||
            forbiddenFileExtensions.includes(file)
          )
      );
      /**
       * @function onFileSuccess - reports a file with no error and set uploading to false
       * @param id
       */
      const onFileSuccess = (id: string) => (file: FileType) =>
        dispatch({ type: SUCCESS, payload: { id, file } });

      /**
       * @function onFileError - reports media-api error.
       * @param id
       * Fallback if the UI is unable to catch the error.
       * e.g: the file size after encryption exceeds media-api's size limit
       */
      const onFileError = (id: string) => () =>
        dispatch({ type: ERROR, payload: { id } });

      /**
       * @function onFileProgress - reports the percentage of the uploading file
       * @param id
       * @param percentage
       */
      const onFileProgress = (id: string, percentage: number) =>
        dispatch({
          type: PROGRESS,
          payload: { id, percentage }
        });

      const uploading = readyToUpload.map(file => {
        const id = `valid-${shortid.generate()}`;
        uploadFile(file, onFileSuccess(id), onFileError(id), progress =>
          onFileProgress(id, progress.uploadPercent)
        );
        return {
          id,
          name: file.name,
          size: file.size,
          uploading: true,
          uploadPercent: 0
        };
      });

      const errorsExceedSizeLimit = exceedSizeLimit.map(file => ({
        id: `err-too-big-${shortid.generate()}`,
        name: file.name,
        size: file.size,
        error: true,
        errorType: ERROR_TYPE_SIZE,
        uploading: false
      }));

      const errorsForbiddenExtension = forbiddenFileExtensions.map(file => ({
        id: `err-wrong-ext-${shortid.generate()}`,
        name: file.name,
        size: file.size,
        error: true,
        errorType: ERROR_TYPE_EXTENSION,
        uploading: false
      }));

      const errors = [...errorsExceedSizeLimit, ...errorsForbiddenExtension];
      const mergedFiles = [...uploading, ...errors];

      dispatch({
        type: ADD_FILES,
        payload: { mergedFiles }
      });
    },
    [isDisabled, uploadFile, fileExtensionWhitelistMap, maxFileSize, files]
  );

  const handleDeleteFile = useCallback(
    (id: string) => dispatch({ type: REMOVE_FILE, payload: { id } }),
    []
  );

  const onDragEnter = () => !isDisabled && setDropzoneActive(true);

  const onDragLeave = () => setDropzoneActive(false);

  const handleFileButtonClick = () => {
    dropzoneRef.open();
  };

  const visibilitySwitchLabel =
    props.visibilitySwitchLabel ||
    uiSchema['ui:options']?.visibilitySwitchLabel;
  const showSwitch = !!visibilitySwitchLabel; // Replace with uiSchema.files.options.showVisibilitySwitch
  const switchLabel = visibilitySwitchLabel; // Replace with uiSchema.files.options.visibilitySwitchLabel
  const [showUpload, setShowUpload] = useState(!showSwitch);

  const handleSwitchClick = () => {
    setShowUpload(prevState => !prevState);
  };
  const description =
    props.schema?.description || props.uiSchema['ui:options']?.description;

  return (
    <>
      {showSwitch && (
        <div
          className={cn('flex items-center space-x-2', showUpload && 'mb-4')}
        >
          <Switch
            checked={showUpload}
            data-cy={`file-${props.name}-switch`}
            id={`${fieldId}-switch`}
            onCheckedChange={handleSwitchClick}
          />
          <Label htmlFor={`${fieldId}-switch`}>{switchLabel as string}</Label>
        </div>
      )}
      {showUpload && (
        <>
          <FormLabel
            formContext={props.formContext}
            id={fieldId}
            label={title}
            required={props.required}
          />
          <FieldLabel
            errorsCount={errorsCount}
            fieldName={props.name}
            filesCount={filesCount}
          />
          {description && (
            <FormDescription id={fieldId} rawDescription={description} />
          )}
          <div
            className={cn(
              '@container/FileUpload relative rounded-md border border-input border-dashed bg-card transition-all duration-200',
              dropzoneActive && 'border-primary'
            )}
          >
            <Dropzone
              className={cn(
                'flex w-full flex-col gap-4 p-4 transition-all duration-200',
                dropzoneActive && 'opacity-20'
              )}
              data-cy={`file-${props.name}`}
              disableClick
              inputProps={{
                required,
                // @ts-expect-error - TS is not happy with the data-cy prop
                'data-cy': `file-${props.name}-input`
              }}
              multiple
              onDragEnter={onDragEnter}
              onDragLeave={onDragLeave}
              onDrop={onDropNewFiles}
              ref={e => {
                dropzoneRef = e;
              }}
            >
              {!isDisabled && (
                <section className="my-4 flex w-full flex-row gap-1">
                  <div
                    className="relative z-2 flex grow flex-col items-center justify-center"
                    data-cy="forms-js-dropzone-fileupload"
                  >
                    <div className="mb-5 text-muted-foreground">
                      <CloudUpload size={40} />
                    </div>
                    <Button
                      aria-label="Click to select files"
                      data-cy={`file-${props.name}-open-button`}
                      onClick={handleFileButtonClick}
                      type="button"
                      variant="outline"
                    >
                      Select files
                    </Button>
                    {maxFileSize && (
                      <span className="select-none py-2.5 text-center text-[10px] text-muted-foreground">
                        The maximum file size is {filesize(maxFileSize)}
                      </span>
                    )}
                  </div>
                  <div className="@sm/FileUpload:block hidden @lg:w-1/3">
                    <AcceptedFilesDescription
                      fileExtensionWhitelistMap={fileExtensionWhitelistMap}
                      filesLength={filesCount}
                    />
                  </div>
                </section>
              )}
              {files?.length > 0 && (
                <section className="flex w-full flex-col gap-2">
                  {files?.map(file => (
                    <File
                      disabled={isDisabled}
                      fieldName={props.name}
                      file={file}
                      handleDeleteFile={handleDeleteFile}
                      key={file.id}
                      maxFileSize={maxFileSize}
                    />
                  ))}
                </section>
              )}
            </Dropzone>
          </div>
          <FormHelp id={fieldId} rawHelp={props.uiSchema['ui:help']} />
        </>
      )}
    </>
  );
});
